import React, { useCallback, useMemo, useState } from "react";

import { Button, Popover, Stack, Typography } from "@mui/material";
import { DataItem, SingleCharForm } from "components/deal/Characteristics-bar/Single-char-form/SingeCharForm";
import { clientTypeOptions, currenciesArray } from "constants/index";
import { useClientActions } from "store";
import { DealBarCharacteristic } from "store/types";
import { IClientDetailCharacteristic } from "store/types/client";

import { ClientListCharForm, ListItem, ListOption } from "./ClientListCharForm";

/** The value-level fields a pill edits; persist() adds characteristic_id + neutral deal/investor/round fields. */
type ValueItem = {
  id: number | string;
  value: string;
  currency: string | null;
  percentage: number;
  is_default: boolean;
  is_deleted: boolean;
};

interface Props {
  characteristic: IClientDetailCharacteristic;
  clientId: number;
  /** Display value for the pill trigger (already formatted, incl. Type label / currency). */
  value: string;
  /** Admins/analysts can edit; everyone else sees the popover in read-only mode. */
  canEdit: boolean;
}

/**
 * Editable pill for a client system characteristic. Mirrors the deal Vintage/Geography/Currency
 * pills: click opens a popover with the exact same deal editor (SingleCharForm), which itself
 * branches on data_type / is_multiple. Saving goes through the client POST
 * /clients/{id}/characteristics/data (the same endpoint the Manage drawer uses).
 */
export const ClientCharacteristicPill: React.FC<Props> = ({ characteristic, clientId, value, canEdit }) => {
  const { postClientCharacteristicsDataAction } = useClientActions();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  // Adapt the client characteristic to the DealBarCharacteristic shape SingleCharForm expects.
  // deal_id/characteristic_id/is_currency are filled with neutral values (the client save ignores them).
  const asDealChar = useMemo<DealBarCharacteristic>(
    () => ({
      id: characteristic.id,
      name: characteristic.name,
      is_multiple: characteristic.is_multiple,
      data_type: characteristic.data_type,
      data: characteristic.data.map((entry) => ({
        id: entry.id,
        deal_id: 0,
        characteristic_id: characteristic.id,
        value: entry.value ?? "",
        percentage: entry.percentage,
        is_default: entry.is_default,
        is_currency: false,
        currency: entry.currency
      }))
    }),
    [characteristic]
  );

  // Save via the client's single POST endpoint (same one the drawer uses); send only this characteristic's values.
  const persist = useCallback(
    async (values: ValueItem[]) => {
      await postClientCharacteristicsDataAction(clientId, {
        characteristic_data: values.map((item) => ({
          ...item,
          characteristic_id: characteristic.id,
          deal_id: 0,
          investor_id: null,
          round_id: null
        }))
      });
      handleClose();
    },
    [clientId, characteristic.id, postClientCharacteristicsDataAction]
  );

  // Non-list editor (text/number/currency/…) — the deal SingleCharForm produces DataItem[].
  const handleSubmit = useCallback(
    (state: DataItem[]) =>
      persist(
        state.map((entry) => ({
          id: entry.id,
          value: entry.value,
          currency: entry.currency,
          percentage: entry.percentage,
          is_default: entry.is_default,
          is_deleted: entry.is_deleted
        }))
      ),
    [persist]
  );

  // List editor — single or multiple; ClientListCharForm returns the full row set (incl. is_deleted rows).
  const handleListApply = useCallback(
    (rows: ListItem[]) =>
      persist(
        rows.map((row) => ({
          id: row.id,
          value: row.value,
          currency: null,
          percentage: row.percentage,
          is_default: false,
          is_deleted: row.is_deleted
        }))
      ),
    [persist]
  );

  const listInitialItems = useMemo(
    () => characteristic.data.map((entry) => ({ id: entry.id, value: entry.value ?? "", percentage: entry.percentage })),
    [characteristic.data]
  );

  const isList = characteristic.data_type === "list";

  // list_options aren't in the client response, so list selects source options by characteristic name.
  const listOptions = useMemo<ListOption[]>(() => {
    if (characteristic.name === "Type") return clientTypeOptions;
    if (characteristic.name === "Primary Currency") return currenciesArray.map((code) => ({ label: code, value: code }));
    return [];
  }, [characteristic.name]);

  return (
    <>
      <Button
        disableRipple
        className="trigger"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          color: "#5d7782",
          padding: "0 10px",
          minWidth: "auto",
          fontSize: "15px",
          backgroundColor: "#edf1f9",
          minHeight: "28px",
          maxHeight: "28px"
        }}
      >
        <Stack
          direction="row"
          gap="5px"
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            maxWidth: "200px",
            fontSize: "12px",
            fontWeight: "400"
          }}
        >
          <Typography sx={{ color: "#496682" }}>{characteristic.name}:</Typography>
          <Typography
            sx={{
              color: "#071F35",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden"
            }}
          >
            {value}
          </Typography>
        </Stack>
      </Button>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {isList ? (
          <ClientListCharForm
            charName={characteristic.name}
            options={listOptions}
            isMultiple={characteristic.is_multiple}
            initialItems={listInitialItems}
            viewOnly={!canEdit}
            onApply={handleListApply}
            onCancel={handleClose}
          />
        ) : (
          <SingleCharForm
            characteristic={asDealChar}
            deal_id={0}
            onCancel={handleClose}
            onSubmit={handleSubmit}
            viewOnly={!canEdit}
          />
        )}
      </Popover>
    </>
  );
};
